import React, { useEffect, useState } from 'react';
import { getMySubscription } from '../Api/Subscriptionapi';
import PaymentModal from '../Components/PaymentModal';
import '../Style/Pricing.css';

const TRIAL_DAYS = 3;
const FLASH_KEY = 'mlink_flash';

const BILLING_TERMS = [
  { key: 'monthly', label: 'Monthly', cadence: '/ month', billingDetails: 'Billed monthly' },
  { key: '3_months', label: '3 Months', cadence: '/ 3 months', billingDetails: 'Billed every 3 months' },
  { key: '6_months', label: '6 Months', cadence: '/ 6 months', billingDetails: 'Billed every 6 months' },
  { key: '1_year', label: '12 Months', cadence: '/ 12 months', billingDetails: 'Billed yearly' },
];

const PLAN_DISPLAY = [
  {
    key: 'basic',
    label: 'Basic',
    prices: { monthly: 200, '3_months': 540, '6_months': 1080, '1_year': 2160 },
    features: [
      '2 profile links',
      '1 social media link',
      '5 templates per category',
      'Basic customization',
    ],
  },
  {
    key: 'standard',
    label: 'Standard',
    prices: { monthly: 380, '3_months': 1050, '6_months': 2100, '1_year': 4200 },
    popular: true,
    features: [
      '5 profile links',
      '3 social media links',
      '10 templates per category',
      'Basic customization',
    ],
  },
  {
    key: 'pro',
    label: 'Pro',
    prices: { monthly: 450, '3_months': 1200, '6_months': 2400, '1_year': 4800 },
    features: [
      'Unlimited profile links',
      'Unlimited social media links',
      '25 templates per category',
      'Basic customization',
    ],
  },
];

const PLAN_TYPE_LABELS = Object.fromEntries(
  PLAN_DISPLAY.flatMap((plan) =>
    BILLING_TERMS.map((term) => [
      `${plan.key}_${term.key}`,
      `${plan.label} · ${term.label}`,
    ])
  )
);

const LEGACY_PLAN_LABELS = {
  monthly: 'Basic · Monthly',
  '3_months': 'Standard · 3 Months',
  '6_months': 'Pro · 6 Months',
  '1_year': 'Pro · 12 Months',
};

const planTypeFor = (tier, term) => `${tier}_${term}`;
const displayPlanPrice = (price) => Math.max(0, Number(price) - 1);
const planLabelFor = (planType) =>
  PLAN_TYPE_LABELS[planType] || LEGACY_PLAN_LABELS[planType] || planType;

const STORAGE_KEY = 'mlink_subscription';
// Set by Dashboard's "Change plan" button so this page doesn't auto-redirect
// back to the Dashboard while the user is choosing a new plan.
const CHANGE_FLAG = 'mlink_changing_plan';

function readCachedSubscription() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheSubscription(subscription) {
  if (subscription) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subscription));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function isChangingPlan() {
  try {
    return sessionStorage.getItem(CHANGE_FLAG) === '1';
  } catch {
    return false;
  }
}

function clearChangingPlan() {
  try {
    sessionStorage.removeItem(CHANGE_FLAG);
  } catch {
    /* ignore */
  }
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function Pricing({ onBack, onLogout, onPlanActivated }) {
  const [subscription, setSubscription] = useState(readCachedSubscription);
  const [loading, setLoading] = useState(true);
  const [previewKey, setPreviewKey] = useState(null);
  const [billingTerm, setBillingTerm] = useState('monthly');
  const [payPlan, setPayPlan] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  // One-time message left by the Dashboard (for example after cancelling).
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem(FLASH_KEY);
      if (msg) {
        sessionStorage.removeItem(FLASH_KEY);
        setFlash(msg);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refreshFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applySubscription(sub) {
    setSubscription(sub);
    cacheSubscription(sub);
  }

  async function refreshFromServer() {
    try {
      const sub = await getMySubscription();
      applySubscription(sub);
      // Skip the auto-redirect when the user came here to change their plan.
      if (
        sub &&
        ['trialing', 'active'].includes(sub.status) &&
        onPlanActivated &&
        !isChangingPlan()
      ) {
        onPlanActivated(sub);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Opens the terms + Razorpay payment window. The subscription is created
  // on the server only after the payment is verified there.
  function handleSelectPlan(planKey) {
    setError('');
    if (hasLiveSubscription && subscription.plan_type === planKey) return;
    // The payment window asks the server, which decides if a switch is allowed
    // and explains why when it isn't.
    setPayPlan(planKey);
  }

  function handlePaymentDone(sub) {
    applySubscription(sub);
    clearChangingPlan();
    setPayPlan(null);
    if (onPlanActivated) onPlanActivated(sub);
  }

  function handleBack() {
    clearChangingPlan();
    if (onBack) onBack();
  }

  function handleLogout() {
    clearChangingPlan();
    if (onLogout) onLogout();
  }

  function openFeatures(planKey) {
    setPreviewKey(planKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function closeFeatures() {
    setPreviewKey(null);
  }

  const hasLiveSubscription =
    subscription && ['trialing', 'active'].includes(subscription.status);

  function buttonLabel(planType) {
    if (hasLiveSubscription && subscription.plan_type === planType) {
      return 'Current plan';
    }
    if (hasLiveSubscription) {
      return 'Switch plan';
    }
    if (subscription && subscription.status === 'past_due') {
      return 'Retry payment';
    }
    return 'Start free trial';
  }

  const previewPlan = previewKey
    ? PLAN_DISPLAY.find((p) => p.key === previewKey)
    : null;
  const previewPlanType = previewPlan ? planTypeFor(previewPlan.key, billingTerm) : null;

  const previewIsCurrent =
    previewPlan &&
    hasLiveSubscription &&
    subscription.plan_type === previewPlanType;

  return (
    <section className="mlink-pricing">
      <div className="mlink-pricing__inner">
        <div className="mlink-pricing__topbar">
          <button type="button" className="mlink-pricing__back" onClick={handleBack}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>

          <button type="button" className="mlink-pricing__logout" onClick={handleLogout}>
            Log out
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        <div className="mlink-pricing__intro">
          <p className="mlink-pricing__eyebrow">MLink</p>
          <h1 className="mlink-pricing__headline">Choose your plan</h1>
          <p className="mlink-pricing__sub">
            Choose the plan and billing period that work for you. All prices are in Indian rupees.
            New subscribers get a {TRIAL_DAYS}-day trial for &#8377;1.
          </p>
        </div>

        <div className="mlink-pricing__terms" role="group" aria-label="Billing period">
          {BILLING_TERMS.map((term) => (
            <button
              type="button"
              key={term.key}
              className={`mlink-pricing__term${billingTerm === term.key ? ' mlink-pricing__term--active' : ''}`}
              aria-pressed={billingTerm === term.key}
              onClick={() => setBillingTerm(term.key)}
            >
              {term.label}
            </button>
          ))}
        </div>

        {subscription && (
          <div className={`mlink-pricing__status mlink-pricing__status--${subscription.status}`}>
            <div>
              <p className="mlink-pricing__status-label">
                {subscription.status === 'trialing' && 'Free trial active'}
                {subscription.status === 'active' && 'Subscription active'}
                {subscription.status === 'past_due' && 'Payment failed'}
                {subscription.status === 'cancelled' && 'Subscription cancelled'}
                {subscription.status === 'expired' && 'Subscription expired'}
              </p>
              <p className="mlink-pricing__status-detail">
                {planLabelFor(subscription.plan_type)}{' '}
                &middot;{' '}
                {subscription.status === 'trialing'
                  ? `Trial ends ${formatDate(subscription.trial_end_date)}`
                  : subscription.status === 'past_due'
                  ? 'Choose a plan to try again'
                  : subscription.status === 'cancelled' || subscription.status === 'expired'
                  ? 'Choose a plan to start again'
                  : `Renews ${formatDate(subscription.subscription_end_date)}`}
              </p>
            </div>
          </div>
        )}

        {flash && <p className="mlink-pricing__notice">{flash}</p>}
        {error && <p className="mlink-pricing__error">{error}</p>}

        {previewPlan ? (
          <div className="mlink-selected">
            <div className="mlink-selected__header">
              <p className="mlink-selected__eyebrow">Your Selected Plan</p>
              {previewPlan.popular && <span className="mlink-selected__badge">Most popular</span>}
              <h2 className="mlink-selected__name">{previewPlan.label}</h2>
              <p className="mlink-selected__price">
                &#8377;{displayPlanPrice(previewPlan.prices[billingTerm])}
                <span>{BILLING_TERMS.find((term) => term.key === billingTerm)?.cadence}</span>
              </p>
              <p className="mlink-selected__billing">
                {BILLING_TERMS.find((term) => term.key === billingTerm)?.billingDetails}
              </p>
              <span className="mlink-selected__trial">
                MLink trial: {TRIAL_DAYS} days &middot; &#8377;1 today
              </span>
            </div>

            <div className="mlink-selected__body">
              <p className="mlink-selected__heading">What&rsquo;s included</p>

              <ul className="mlink-selected__features">
                {previewPlan.features.map((feature) => (
                  <li key={feature}>
                    <span className="mlink-selected__check" aria-hidden="true">
                      &#10003;
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <p className="mlink-selected__note">
                This is your MLink plan price. Review the billing and trial terms before confirming
                payment.
              </p>

              <div className="mlink-selected__actions">
                <button type="button" className="mlink-selected__change" onClick={closeFeatures}>
                  Change Plan
                </button>
                <button
                  type="button"
                  className="mlink-selected__continue"
                  disabled={loading || previewIsCurrent}
                  onClick={() => handleSelectPlan(previewPlanType)}
                >
                  {previewIsCurrent ? 'Current plan' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mlink-pricing__grid">
            {PLAN_DISPLAY.map((plan) => {
              const planType = planTypeFor(plan.key, billingTerm);
              const isCurrent = hasLiveSubscription && subscription.plan_type === planType;
              return (
                <div
                  className={`mlink-plan${isCurrent ? ' mlink-plan--current' : ''}${plan.popular ? ' mlink-plan--popular' : ''}`}
                  key={plan.key}
                >
                  {plan.popular && <span className="mlink-plan__ribbon">Most Popular</span>}
                  <p className="mlink-plan__name">{plan.label}</p>
                  <p className="mlink-plan__price">
                    &#8377;{displayPlanPrice(plan.prices[billingTerm])}
                    <span>{BILLING_TERMS.find((term) => term.key === billingTerm)?.cadence}</span>
                  </p>
                  <p className="mlink-plan__billing">
                    {BILLING_TERMS.find((term) => term.key === billingTerm)?.billingDetails}
                  </p>
                  <p className="mlink-plan__trial">MLink trial: {TRIAL_DAYS} days for &#8377;1</p>

                  <div className="mlink-plan__actions">
                    <button
                      type="button"
                      className="mlink-plan__features-btn"
                      onClick={() => openFeatures(plan.key)}
                    >
                      View Features
                    </button>
                    <button
                      type="button"
                      className="mlink-plan__cta"
                      disabled={isCurrent || loading}
                      onClick={() => handleSelectPlan(planType)}
                    >
                      {buttonLabel(planType)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {payPlan && (
        <PaymentModal
          planKey={payPlan}
          onClose={() => setPayPlan(null)}
          onSuccess={handlePaymentDone}
        />
      )}
    </section>
  );
}